import { 
  User, 
  Users, 
  FileText, 
  Activity, 
  AlertCircle, 
  Zap, 
  Heart, 
  UserCheck,
  CheckCircle2,
  Circle,
  ArrowLeft,
  Send
} from 'lucide-react';
import { cn } from '../../lib/utils';
import type { PRFSectionStatus } from '../../types/prf';

interface PRFSection {
  id: string;
  name: string;
  icon: typeof User;
  status: PRFSectionStatus;
  color: string;
  bgColor: string;
  borderColor: string;
}

interface PRFMainScreenProps {
  incidentId: string;
  eventNumber?: string;
  prfRef?: string;
  onSectionSelect: (sectionId: string) => void;
  onBack: () => void;
  onSubmit: () => void;
  sectionStatuses?: Record<string, PRFSectionStatus>;
}

export default function PRFMainScreen({
  incidentId,
  eventNumber,
  prfRef,
  onSectionSelect,
  onBack,
  onSubmit,
  sectionStatuses = {},
}: PRFMainScreenProps) {
  const sections: PRFSection[] = [
    {
      id: 'patientDetails',
      name: 'Patient Details',
      icon: User,
      status: sectionStatuses.patientDetails || 'not_started',
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
    },
    {
      id: 'nokDetails',
      name: 'NOK Details',
      icon: Users,
      status: sectionStatuses.nokDetails || 'not_started',
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
    },
    {
      id: 'presentingComplaint',
      name: 'Presenting Complaint',
      icon: FileText,
      status: sectionStatuses.presentingComplaint || 'not_started',
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/30',
    },
    {
      id: 'pastMedicalHistory',
      name: 'Past Medical History',
      icon: Activity,
      status: sectionStatuses.pastMedicalHistory || 'not_started',
      color: 'text-cyan-400',
      bgColor: 'bg-cyan-500/10',
      borderColor: 'border-cyan-500/30',
    },
    {
      id: 'allergiesMeds',
      name: 'Allergies/Meds',
      icon: AlertCircle,
      status: sectionStatuses.allergiesMeds || 'not_started',
      color: 'text-red-400',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/30',
    },
    {
      id: 'fast',
      name: 'F.A.S.T',
      icon: Zap,
      status: sectionStatuses.fast || 'not_started',
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-500/10',
      borderColor: 'border-yellow-500/30',
    },
    {
      id: 'observations',
      name: 'OBS',
      icon: Heart,
      status: sectionStatuses.observations || 'not_started',
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/30',
    },
    {
      id: 'attending',
      name: 'Attending',
      icon: UserCheck,
      status: sectionStatuses.attending || 'not_started',
      color: 'text-pink-400',
      bgColor: 'bg-pink-500/10',
      borderColor: 'border-pink-500/30',
    },
  ];

  const getStatusIcon = (status: PRFSectionStatus) => {
    switch (status) {
      case 'complete':
        return <CheckCircle2 className="w-4 h-4 text-green-400" />;
      case 'incomplete':
        return <Circle className="w-4 h-4 text-yellow-400" />;
      default:
        return <Circle className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const completedSections = sections.filter(s => s.status === 'complete').length;
  const totalSections = sections.length;
  const progressPercentage = (completedSections / totalSections) * 100;

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-card">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-1.5 rounded-lg hover:bg-accent transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-foreground">Patient Report Form</h1>
              {prfRef && (
                <p className="text-[11px] font-semibold text-primary">{prfRef}</p>
              )}
              <p className="text-xs text-muted-foreground">
                {eventNumber && `Event: ${eventNumber} • `}Incident: {incidentId}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs font-semibold text-foreground">
              {completedSections} / {totalSections} Complete
            </p>
            <div className="w-24 h-1.5 bg-muted rounded-full mt-1 overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Sections Grid */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => onSectionSelect(section.id)}
                className={cn(
                  'relative p-4 rounded-xl border-2 transition-all duration-200',
                  'hover:scale-[1.02] active:scale-[0.98]',
                  'flex flex-col items-center gap-2 text-center',
                  section.bgColor,
                  section.borderColor,
                  'hover:shadow-lg'
                )}
              >
                {/* Status Badge */}
                <div className="absolute top-2 right-2">
                  {getStatusIcon(section.status)}
                </div>

                {/* Icon */}
                <div className={cn('p-3 rounded-lg', section.bgColor)}>
                  <Icon className={cn('w-6 h-6', section.color)} />
                </div>

                {/* Label */}
                <span className="text-sm font-semibold text-foreground">
                  {section.name}
                </span>

                {/* Status Text */}
                <span className="text-[10px] text-muted-foreground capitalize">
                  {section.status.replace('_', ' ')}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Footer with Submit Button */}
      <div className="flex-shrink-0 border-t border-border bg-card p-4">
        <button
          onClick={onSubmit}
          disabled={completedSections < totalSections}
          className={cn(
            'w-full py-3 rounded-lg font-semibold text-sm transition-all',
            'flex items-center justify-center gap-2',
            completedSections === totalSections
              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
              : 'bg-muted text-muted-foreground cursor-not-allowed'
          )}
        >
          <Send className="w-4 h-4" />
          Submit PRF
          {completedSections < totalSections && (
            <span className="text-xs">
              ({totalSections - completedSections} sections remaining)
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
